import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Layers, ArrowDown, Check } from 'lucide-react';
import { Item, checkMissing } from './item.jsx';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { getAllLoopsName, getShortLoopName } from '../utils.js';

// Continuous flow renders the whole resolved path on one page instead of one item
// at a time. The walk it consumes comes from `engine.traverse()`, so the list can
// never diverge from the flow's real branching: it ends at the frontier (the first
// unanswered question), and grows as answers are given.

// An entry only counts as "in a loop" once that loop actually has an iteration.
// The walk also covers items further down the flow whose loop has not started
// yet: those carry a loopName but no loopIndex, and must render as plain items
// rather than as a phantom iteration.
export function hasIteration(entry) {
  return !!(entry && entry.loopName && entry.loopIndex !== undefined && entry.loopIndex !== null);
}

// The walk from traverse() runs as far as the flow's branching can be *computed*,
// which is not the same as how far the user has got: answering a question further
// down out of order resolves its branch and makes items appear well below where the
// user is working. Cut the list at the first item still missing a required answer so
// the page can only ever grow top-down, one question at a time.
//
// The engine's own position is never cut away: `Continue` acts from there, and an
// item above it can become incomplete again when its fields are rebuilt from a
// changed earlier answer.
export function visibleUpToFrontier(entries, currentEntryKey) {
  const all = entries || [];
  const frontier = all.findIndex((e) => checkMissing(e.item, e.values));
  if (frontier === -1) return all;
  let currentIndex = -1;
  all.forEach((e, i) => { if (entryKeyOf(e) === currentEntryKey) currentIndex = i; });
  return all.slice(0, Math.max(frontier, currentIndex) + 1);
}

// Consecutive entries sharing the same loop *and* iteration form one block, which
// is what gets an iteration header. Non-loop entries group into plain blocks.
export function groupIntoBlocks(entries) {
  const blocks = [];
  let current = null;
  (entries || []).forEach((entry, entryIndex) => {
    const inLoop = hasIteration(entry);
    const loopName = inLoop ? entry.loopName : undefined;
    const loopIndex = inLoop ? entry.loopIndex : undefined;
    // Belongs to a loop the flow has not entered yet: no iteration exists to store
    // an answer in, so these are displayed read-only.
    const pendingLoop = !inLoop && !!entry.loopName;
    if (!current
        || current.loopName !== loopName
        || current.loopIndex !== loopIndex
        || current.pendingLoop !== pendingLoop) {
      current = {
        id: `cf-block-${blocks.length}`,
        loopName,
        loopIndex,
        pendingLoop,
        depth: loopName ? loopDepth(loopName) : 0,
        entries: [],
        firstEntryIndex: entryIndex,
      };
      blocks.push(current);
    }
    current.entries.push(entry);
  });
  return blocks;
}

// Identifies one entry in the walk (an item, at a given iteration if looped) so the
// engine's current position can be matched against what is rendered.
export function entryKeyOf(entry) {
  if (!entry) return undefined;
  return hasIteration(entry)
    ? `${entry.item.id}@${entry.loopName}#${entry.loopIndex}`
    : entry.item.id;
}

// Stable DOM id for one entry of the walk. Navigation living outside this component
// — the left-hand form path — needs a handle on an individual question, not just on
// the iteration block it belongs to.
export function entryDomId(entryKey) {
  if (!entryKey) return undefined;
  // '@' and '#' are legal in an id but would have to be escaped in a selector; the
  // lookups below use getElementById, and a sanitised id keeps it usable either way.
  return `cf-entry-${String(entryKey).replace(/[^\w-]/g, '_')}`;
}

// Scrolls a continuous-mode question into view. Returns false when that entry is not
// on the page (below the reveal frontier, or the flow is not in continuous mode), so
// the caller can fall back to moving the engine instead.
export function scrollToEntryKey(entryKey) {
  const id = entryDomId(entryKey);
  const el = id ? document.getElementById(id) : null;
  if (!el || typeof el.scrollIntoView !== 'function') return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
}

// Loop keys encode nesting with '#': `parent` -> 0, `parent#child` -> 1.
export function loopDepth(loopName) {
  if (!loopName) return 0;
  return getAllLoopsName(loopName).length - 1;
}

// One row per distinct loop, in order of first appearance, listing the iterations
// present in the rendered walk. Drives the quick-jump navigator.
export function buildLoopOutline(blocks) {
  const byLoop = new Map();
  blocks.forEach((block) => {
    if (!block.loopName) return;
    if (!byLoop.has(block.loopName)) {
      byLoop.set(block.loopName, {
        loopName: block.loopName,
        depth: block.depth,
        iterations: [],
      });
    }
    const row = byLoop.get(block.loopName);
    if (!row.iterations.some((it) => it.loopIndex === block.loopIndex)) {
      row.iterations.push({ loopIndex: block.loopIndex, blockId: block.id });
    }
  });
  return Array.from(byLoop.values());
}

function scrollToBlock(blockId) {
  const el = document.getElementById(blockId);
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

// Sticky quick-jump navigator: which loops are in play, which iteration of each you
// are reading, and one click to any other iteration.
function LoopNavigator({ outline, loopLabel, iterationLabel, activeBlockId, currentIterationOf }) {
  if (outline.length === 0) return null;
  return (
    <div className="no-print sticky top-0 z-30 bg-white/95 backdrop-blur border border-gray-200 rounded-2xl p-3 mb-4 space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
        <Layers className="w-3.5 h-3.5" />
        Loop navigation
      </div>
      {outline.map((row) => (
        <div
          key={`nav-${row.loopName}`}
          className="flex flex-wrap items-center gap-1.5"
          style={{ paddingLeft: row.depth * 16 }}
        >
          {row.depth > 0 && (
            <span className="text-gray-500/50 text-xs" aria-hidden="true">↳</span>
          )}
          <span className="text-sm text-gray-500 mr-1">
            {loopLabel(row.loopName)}
            {row.depth > 0 && (
              <span className="ml-1 text-xs opacity-60">(nested level {row.depth + 1})</span>
            )}
          </span>
          {row.iterations.map((it) => {
            const isActive = it.blockId === activeBlockId;
            const isCurrent = currentIterationOf(row.loopName) === it.loopIndex;
            return (
              <button
                key={`nav-${row.loopName}-${it.loopIndex}`}
                type="button"
                onClick={() => scrollToBlock(it.blockId)}
                aria-current={isActive ? 'true' : undefined}
                title={iterationLabel(row.loopName, it.loopIndex)}
                className={cn(
                  'max-w-[14rem] truncate rounded-md border px-2 py-0.5 text-xs transition-colors',
                  isActive
                    ? 'border-primary-900 bg-primary-900 text-on-primary'
                    : 'border-gray-200 bg-white text-primary-900 hover:border-primary-900 hover:bg-primary-900 hover:text-on-primary'
                )}
              >
                {iterationLabel(row.loopName, it.loopIndex)}
                {isCurrent && !isActive && <span className="ml-1 opacity-70">•</span>}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Header above each loop iteration: indented by nesting depth, names the loop, the
// iteration, and its position — so the reader always knows where they are.
function IterationHeader({ block, loopLabel, iterationLabel, totalIterations, isCurrent }) {
  // Deliberately not sticky: the loop navigator above already keeps this context
  // visible, and a second sticky layer overlapped the item titles while scrolling.
  return (
    <div
      className="flex flex-wrap items-center gap-2 py-1.5 px-3 mb-2 bg-primary-50 border-l-4 border-primary-900 rounded-r-lg"
      style={{ marginLeft: block.depth * 16 }}
    >
      {block.depth > 0 && (
        <span className="text-gray-500/60 text-xs" aria-hidden="true">↳</span>
      )}
      <span className="text-sm font-medium text-primary-900">
        {loopLabel(block.loopName)}
      </span>
      <span className="text-sm text-gray-500">
        {iterationLabel(block.loopName, block.loopIndex)}
      </span>
      <span className="text-xs text-gray-500/80">
        (iteration {block.loopIndex}
        {totalIterations ? ` of ${totalIterations}` : ''}
        {block.depth > 0 ? `, nested level ${block.depth + 1}` : ''})
      </span>
      {isCurrent && (
        <span className="ml-auto text-xs px-2 py-0.5 rounded-md bg-primary-900 text-on-primary">
          current
        </span>
      )}
    </div>
  );
}

export function ContinuousFlow({
  entries,
  isObserver,
  readOnly,
  loopLabel,
  iterationLabel,
  totalIterationsOf,
  currentIterationOf,
  onChange,
  onItemPreliminaryChange,
  onAdvance,
  canAdvance = true,
  isComplete = false,
  currentItemId,
  currentEntryKey,
}) {
  const blocks = useMemo(() => groupIntoBlocks(entries), [entries]);
  const outline = useMemo(() => buildLoopOutline(blocks), [blocks]);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const containerRef = useRef(null);
  // `entries` is a fresh array on every keystroke (traverse rebuilds it), so the
  // observer is keyed on the block *structure* instead — otherwise it would tear
  // down and re-observe every block on each character typed.
  const blockStructure = blocks.map((b) => b.id).join('|');

  // Scroll-spy: highlight the iteration currently being read in the navigator.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || blocks.length === 0) return undefined;
    const observer = new IntersectionObserver(
      (observed) => {
        const visible = observed
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveBlockId(visible[0].target.id);
      },
      { rootMargin: '-120px 0px -60% 0px', threshold: 0 }
    );
    blocks.forEach((block) => {
      const el = document.getElementById(block.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockStructure]);

  return (
    <div ref={containerRef} className="continuousFlow">
      <LoopNavigator
        outline={outline}
        loopLabel={loopLabel}
        iterationLabel={iterationLabel}
        activeBlockId={activeBlockId}
        currentIterationOf={currentIterationOf}
      />

      <div className="flex flex-col gap-6">
        {blocks.map((block) => (
          <div key={block.id} id={block.id}>
            {/* Items of a repeating section the flow has not reached yet are shown
                for context but not editable: their iteration does not exist, so an
                answer here would have nowhere to be stored. */}
            {block.pendingLoop && (
              <p className="text-xs text-gray-500 mb-1 px-3">
                Part of a repeating section — editable once you continue to it.
              </p>
            )}
            {block.loopName && (
              <IterationHeader
                block={block}
                loopLabel={loopLabel}
                iterationLabel={iterationLabel}
                totalIterations={totalIterationsOf(block.loopName)}
                isCurrent={currentIterationOf(block.loopName) === block.loopIndex}
              />
            )}
            <div
              className={cn('flex flex-col gap-4')}
              style={block.depth > 0 ? { marginLeft: block.depth * 16 } : undefined}
            >
              {block.entries.map((entry, indexInBlock) => (
                <div
                  key={`cf-${entry.item.id}-${block.loopName || ''}-${block.loopIndex || 0}-${indexInBlock}`}
                  id={entryDomId(entryKeyOf(entry))}
                  // scroll-mt clears the sticky menu and loop navigator, which would
                  // otherwise sit on top of the question just scrolled to.
                  className="relative scroll-mt-32"
                >
                  {entryKeyOf(entry) === currentEntryKey && (
                    <span className="absolute -top-2 right-3 z-10 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-primary-900 text-on-primary">
                      current item
                    </span>
                  )}
                <Item
                  item={entry.item}
                  loopIndex={entry.loopIndex}
                  isObserver={isObserver}
                  readOnly={readOnly || block.pendingLoop}
                  // "You are here": the item the engine sits on, i.e. what Continue
                  // acts from.
                  highlighted={entryKeyOf(entry) === currentEntryKey}
                  values={entry.values}
                  preliminaries={entry.preliminaries}
                  hasErrors={entry.item.errors && entry.item.errors.length > 0}
                  preliminarySection={false}
                  showApplyToAll={false}
                  // Progression is driven by the single control below the list.
                  showSubmit={false}
                  autoFocusFirstField={false}
                  onChange={(fieldIndex, value) =>
                    onChange(entry, fieldIndex, 'values', value)}
                  onItemPreliminaryChange={(fieldIndex, value) =>
                    onItemPreliminaryChange(entry, fieldIndex, 'preliminaries', value)}
                />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="flex flex-col items-center gap-2 mt-6">
          {isComplete ? (
            <p className="flex items-center gap-1.5 text-sm text-gray-500">
              <Check className="w-4 h-4" /> All items answered.
            </p>
          ) : (
            <>
              <Button onClick={onAdvance} disabled={!canAdvance}>
                <ArrowDown className="w-4 h-4 mr-1.5" />
                Continue
              </Button>
              <p className="text-xs text-gray-500 text-center max-w-md">
                {canAdvance
                  ? 'Moves the flow past the highlighted item — adding loop iterations and later items as they are reached.'
                  : 'Answer the required fields on the highlighted item to continue.'}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export { getShortLoopName };
